import type { ReactNode } from 'react';
import { catTime, utcTooltip } from '@/lib/format';

export function Card({ title, actions, children, className = '' }: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-header flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink-800">{title}</h2>
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

/** Every timestamp is shown in CAT, with UTC one hover away (spec §13). */
export function Timestamp({ value }: { value: string | null | undefined }) {
  return (
    <time className="tabular text-ink-700" title={utcTooltip(value)}>
      {catTime(value)}
    </time>
  );
}

export function Reference({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-ink-400">—</span>;
  return <span className="mono text-ink-800">{value}</span>;
}

export function Money({ amount, currency }: { amount: string | null | undefined; currency?: string | null }) {
  return (
    <span className="tabular tracking-tight">
      {currency && <span className="mr-1 text-ink-500">{currency}</span>}
      {amount ?? '—'}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-10 text-center text-sm text-ink-500">{children}</p>;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <p className="px-4 py-10 text-center text-sm text-ink-500">{label}…</p>;
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <p className="m-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {message}
    </p>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="label">{label}</div>
      <div className="mt-0.5 text-sm text-ink-900">{children}</div>
    </div>
  );
}

export function Banner({ tone, children }: { tone: 'danger' | 'warn' | 'info'; children: ReactNode }) {
  const styles = {
    danger: 'border-red-300 bg-red-50 text-red-900',
    warn: 'border-amber-300 bg-amber-50 text-amber-900',
    info: 'border-sky-300 bg-sky-50 text-sky-900',
  } as const;
  return (
    <div className={`rounded-md border px-4 py-3 text-sm ${styles[tone]}`}>{children}</div>
  );
}
